import { memo, useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/ui/accordion';
import { cn } from 'lib/utils';
import { ScrollArea } from 'components/ui/scroll-area';
import ReportActions from './ReportActions';
import Loader from 'components/Loader';
import { useGetVoiceReportDetail } from 'services/voice-scan/hooks';
import { VoiceReadingData } from 'types/voice';
import ScanVitalDetail from './ScanVitalDetail';

import NoData from 'assets/no-data.png';

interface ScanDetailProps {
  className?: string;
  containerClassName?: string;
  readingId: string;
  isLoading?: boolean;
  refetchScanReports?: () => void;
  isDetail?: boolean;
}

const ScanDetail = ({
  className,
  containerClassName,
  readingId,
  isLoading,
  isDetail = false,
  refetchScanReports,
}: ScanDetailProps) => {
  const { t } = useTranslation();
  const isMediaMobile = useMediaPredicate('(max-width: 640px)');

  const [searchParams, setSearchParams] = useSearchParams();
  const vitalKey = searchParams.get('vital') || '';

  const [selectedVital, setSelectedVital] = useState<VoiceReadingData>();

  const { data: voiceReportDetail, isFetching } = useGetVoiceReportDetail({
    sessionId: readingId,
  });

  const categorizedVoiceData = useMemo(() => {
    if (!voiceReportDetail) {
      return null;
    }

    const data: Record<string, VoiceReadingData[]> = {};
    Object.entries(voiceReportDetail?.sub_categorization).forEach(
      ([key, categoryKeys]) => {
        const categoryValues: VoiceReadingData[] = [];
        categoryKeys.forEach((categoryKey) => {
          const configData = voiceReportDetail.scale_config?.[categoryKey];
          const scanData = voiceReportDetail?.voice_scan_report?.find(
            (report) => report.key === categoryKey
          );

          if (configData && scanData) {
            categoryValues.push({
              display: configData.display,
              key: scanData?.key,
              long_intro: configData.long_intro,
              colorRange: configData.color_range ?? [],
              short_intro: configData.short_intro,
              category: scanData?.category ?? '',
              color: scanData?.color,
              value: scanData?.value ?? 0,
            });
          }
        });
        data[key] = categoryValues;
      }
    );

    return data;
  }, [voiceReportDetail]);

  const scanData = useMemo(() => {
    if (!categorizedVoiceData || !Object.keys(categorizedVoiceData)?.length) {
      return [];
    }

    return Object.values(categorizedVoiceData).flat();
  }, [categorizedVoiceData]);

  const onVitalSelect = (vitalKey: string) => {
    if (
      !categorizedVoiceData ||
      (selectedVital && selectedVital.key === vitalKey)
    ) {
      setSelectedVital(undefined);
      return;
    }

    const vital = scanData?.find((data) => data.key === vitalKey);
    setSelectedVital(vital);
  };

  useEffect(() => {
    if (vitalKey && scanData?.length) {
      onVitalSelect(vitalKey);
      if (!isMediaMobile) setSearchParams({});
    }
  }, [vitalKey, scanData, isMediaMobile]);

  if (isLoading || isFetching) {
    return <Loader />;
  }

  if (!categorizedVoiceData || !Object.keys(categorizedVoiceData)?.length) {
    return (
      <div className='flex flex-col items-center justify-center h-full lg:border rounded-2xl border-primary'>
        <div className='size-40'>
          <img
            src={NoData}
            alt='no data'
            className='object-cover w-full h-full'
          />
        </div>
        <p>{t('no_scan_data')}</p>
      </div>
    );
  }

  return (
    <>
      {!isDetail ? (
        <ReportActions
          readingId={readingId}
          scanData={voiceReportDetail}
          refetchScanReports={refetchScanReports}
        />
      ) : null}

      <div
        className={cn(
          'lg:border rounded-2xl border-primary',
          containerClassName
        )}
        id='vital-signs'
      >
        <div className='py-6 lg:pl-6'>
          <div className='grid grid-cols-12'>
            <ScrollArea
              className={cn(
                'transition-all duration-300',
                selectedVital ? 'col-span-7 pr-4' : 'col-span-12 pr-6',
                className
              )}
            >
              <div className='flex flex-col space-y-8'>
                {Object.keys(categorizedVoiceData)?.map((category) => {
                  return (
                    <div key={category}>
                      <h2 className='text-xl font-semibold'>{category}</h2>

                      {categorizedVoiceData[category]?.length ? (
                        <div className='p-4 mt-2 ml-2 border-l-2 border-l-primary'>
                          {categorizedVoiceData[category]?.map((vital) => (
                            <ScanVitalDetail
                              vital={vital}
                              onSelect={onVitalSelect}
                              key={vital.key}
                              selectedVital={selectedVital}
                              category={category}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <ScrollArea
              className={cn(
                'col-span-5 lg:pl-4 lg:border-l border-dashed border-l-gray-400 lg:pr-6',
                selectedVital ? 'block' : 'hidden',
                className
              )}
            >
              <Accordion type='multiple' defaultValue={['info']}>
                {selectedVital?.colorRange?.length ? (
                  <AccordionItem value='ranges'>
                    <AccordionTrigger>{t('parameter_ranges')}</AccordionTrigger>
                    <AccordionContent>
                      {selectedVital?.colorRange?.map(
                        ({ color, category }, index) => {
                          return (
                            <div
                              key={index}
                              className={cn(
                                'flex items-center justify-between py-2 font-medium border-b',
                                index === 0 && 'border-t'
                              )}
                            >
                              <div className='flex items-center gap-x-2'>
                                <div
                                  className='w-4 h-4 rounded-full'
                                  style={{ backgroundColor: color }}
                                />
                                <p className='text-sm'>{category}</p>
                              </div>
                            </div>
                          );
                        }
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ) : null}
                <AccordionItem value='info'>
                  <AccordionTrigger>{t('parameter_info')}</AccordionTrigger>
                  <AccordionContent>
                    <div className='space-y-4 font-medium'>
                      <p className='text-primary'>
                        {selectedVital?.short_intro}
                      </p>

                      <div
                        className='whitespace-pre-wrap'
                        style={{ wordBreak: 'break-word' }}
                      >
                        {selectedVital
                          ? parse(
                              selectedVital?.long_intro
                                .replace(/<link>/g, '<a>')
                                .replace(/<\/link>/g, '</a>'),
                              {
                                replace(domNode: any) {
                                  const text = domNode?.data;
                                  if (!text) return null;

                                  if (text?.startsWith('https://')) {
                                    return (
                                      <a
                                        key={text}
                                        href={text}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='text-blue-500 '
                                      >
                                        {text}
                                      </a>
                                    );
                                  }
                                },
                              }
                            )
                          : null}
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </ScrollArea>
          </div>
        </div>
      </div>
    </>
  );
};

export default memo(ScanDetail);
